import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  Code2,
  Copy,
  Eye,
  EyeOff,
  RotateCcw,
  Save,
  Maximize2,
  Minimize2,
  RefreshCw,
  CheckCircle2,
  FileCode,
  Braces,
  Wand2,
  Undo2,
  Upload,
  Download,
  Smartphone,
  Tablet,
  Monitor,
  Terminal,
  AlertTriangle,
} from "lucide-react";
import {
  verifyPageHtmlEditAccess,
  getPageHtmlSourceAPI,
  savePageHtmlSourceAPI,
  restorePageGeneratedHtmlAPI,
} from "../../auth/apiPageBuilder";
import { getFirstAnimationSettingsAPI } from "../../auth/Animationsettingsapi";
import { isAuthenticated } from "../../auth";
import { elakayAutoText } from "../dataElakay";
import { PAGE_HTML_SANDBOX, securePageHtmlDocument } from "./pageRendererSecurity";
import {
  getHtmlCssSection,
  getHtmlJsSection,
  getHtmlBodySection,
  setHtmlCssSection,
  setHtmlJsSection,
  setHtmlBodySection,
  formatCssText,
  analyzePageHtml,
  PAGE_BUILDER_HTML_LIMIT,
  PAGE_BUILDER_SANDBOX_SOURCE,
  withConsoleShim,
} from "./pageHtmlDocument";
import "./pageHtmlSourceModal.css";

// "</> Edit HTML Code" -- password-gated direct HTML editing of a PageBuilder
// page (spec: "Edit HTML Code sécurisé par mot de passe"), aligned on the
// CODE-EDITOR experience of /elakay/how-we-do-it-editor/:id's "Console / Edit
// Page" (HowWeDoItCodeEditorModal.jsx + HowWeDoItSandboxedFrame.jsx) --
// tabs, a live sandboxed preview, a Console tab for the preview's own runtime
// errors, Fullscreen, Cmd/Ctrl+S. This does NOT reuse howWeDoIt's exact
// serializer: that document is 3 independent DB fields (htmlCode/customCss/
// customJs); PageBuilder's `htmlSource` is -- and stays -- ONE complete HTML
// document string PER JSON PAGE, exactly like the read-only "Code HTML
// généré" panel already built from buildSiteHtml() elsewhere in
// PageBuilder.jsx. The "HTML" tab below IS that one document (same state,
// same textarea, same save payload as before this change); "CSS"/"JS" are
// pure convenience lenses on top of it (pageHtmlDocument.js) -- never a
// second representation to keep in sync, so there is no JSON<->HTML
// ping-pong risk here at all.
//
// This modal is only ever mounted by a caller that has ALREADY checked
// pageHtmlAccess.canEditPageHtml() -- it never re-derives that permission
// itself. The backend re-checks the SAME permission (canUserEditPageCode)
// plus the password on every request regardless, so hiding/showing this
// modal in React is only ever a UX convenience, never the real security
// boundary.
//
// Flow: every open starts at the password step (never trusts a stale
// in-memory authorization from a previous open) -> password verified via
// POST .../verify-html-edit (the account's REAL password, never a second
// hardcoded one) -> a short-lived confirmationToken is issued and reused for
// Save/Restore within this same open, so the password is never re-asked per
// keystroke -> Apply/Restore both still re-verify server-side.
//
// PER-PAGE overrides (spec: "ajouter de nouvelles pages HTML complètement
// différentes adaptées à la data JSON"): PageBuilder documents can have
// several JSON pages (doc.pages). Each one gets its OWN independent HTML
// override (server: `htmlSourcePages[jsonPageId]`) -- switching the page
// selector below re-loads/re-seeds the editor for that page specifically;
// saving one page's HTML never touches another page's override, and pages
// with no override keep rendering normally from the Visual Builder/JSON
// (PageRenderer.jsx). `getGeneratedHtml(jsonPageId)` is the caller's OWN
// buildSiteHtml() generator scoped to one page -- already resolves
// boundData/shop-live bindings exactly like the whole-site export, so a
// freshly seeded page is "adapted to the JSON data" for free, no second
// generator invented here.
const PREVIEW_DEBOUNCE_MS = 900;

// A 404 here specifically means the PageBuilder document this modal was
// opened for no longer exists server-side (e.g. deleted from another
// tab/session while this one stayed open with a now-stale pageId) -- never
// a wrong password (that's a 401, handled separately). Surfacing the raw
// "Page introuvable." next to a password field or a Save button is
// confusing; this points the admin back to the recovery path instead
// (index.jsx's handleSave/performQuickSave now auto-recreate a page that
// 404s on update, so re-saving from the Builder actually fixes this).
const describeHtmlSourceError = (e, fallback) =>
  e?.status === 404
    ? elakayAutoText(
        "h_pg_html_page_gone",
        "This page no longer exists on the server (it may have been deleted). Close this window, click Save in the Builder to re-save it, then try again."
      )
    : e?.message || fallback;

const TABS = [
  { id: "full", icon: Code2, labelKey: "h_pg_html_tab_full", label: "Full Page" },
  { id: "html", icon: FileCode, labelKey: "h_pg_html_tab_html", label: "HTML" },
  { id: "css", icon: Braces, labelKey: "h_pg_html_tab_css", label: "CSS" },
  { id: "js", icon: Braces, labelKey: "h_pg_html_tab_js", label: "JavaScript" },
  { id: "console", icon: Terminal, labelKey: "h_pg_html_tab_console", label: "Console" },
];

const DEVICE_MODES = [
  { id: "desktop", icon: Monitor, width: "100%", label: "Desktop" },
  { id: "tablet", icon: Tablet, width: "768px", label: "Tablet" },
  { id: "mobile", icon: Smartphone, width: "390px", label: "Mobile" },
];

const downloadText = (filename, content) => {
  const blob = new Blob([content], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const PageHtmlSourceModal = ({
  show,
  onHide,
  pageId,
  pageTitle,
  jsonPages,
  initialJsonPageId,
  htmlSourcePages,
  legacyHtmlSource,
  getGeneratedHtml,
  onCreatePage,
  onSaveDocument,
  onSaved,
  onApplied,
}) => {
  const { token } = isAuthenticated() || {};
  const pagesList = Array.isArray(jsonPages) && jsonPages.length ? jsonPages : [{ id: initialJsonPageId, name: pageTitle || "Page" }];

  const [step, setStep] = useState("password"); // "password" | "editor"
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [confirmationToken, setConfirmationToken] = useState("");
  // AnimationSettings' "Password Required Settings" (passwordRequired.
  // pageBuilder) -- fail-safe default TRUE (see passwordRequiredSettingsService.js's
  // header comment: an unpopulated field or a failed fetch must never
  // silently skip the password step). Checked once per open; the real
  // enforcement is server-side regardless (assertHtmlEditConfirmation), this
  // is only ever a UX convenience to skip straight to the editor.
  const [checkingRequirement, setCheckingRequirement] = useState(true);

  const [selectedJsonPageId, setSelectedJsonPageId] = useState(initialJsonPageId);
  const [creatingPage, setCreatingPage] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savedHtml, setSavedHtml] = useState("");
  const [draftHtml, setDraftHtml] = useState("");
  const [isManuallyEdited, setIsManuallyEdited] = useState(false);
  const [editedAt, setEditedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [copyStatus, setCopyStatus] = useState("");

  const [activeTab, setActiveTab] = useState("full");
  const [showPreview, setShowPreview] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewNonce, setPreviewNonce] = useState(0);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [consoleEntries, setConsoleEntries] = useState([]);
  const [importError, setImportError] = useState("");
  const [applied, setApplied] = useState(false);

  const textareaRef = useRef(null);
  const debounceRef = useRef(null);
  const iframeRef = useRef(null);
  const importInputRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    setStep("password");
    setPassword("");
    setPasswordError("");
    setConfirmationToken("");
    setError("");
    setActiveTab("full");
    setFullscreen(false);
    setPreviewDevice("desktop");
    setConsoleEntries([]);
    setImportError("");
    setApplied(false);
    const startPageId = initialJsonPageId || pagesList[0]?.id;
    setSelectedJsonPageId(startPageId);
    setCheckingRequirement(true);
    let cancelled = false;
    getFirstAnimationSettingsAPI().then((result) => {
      if (cancelled) return;
      // Fail-safe: anything other than the literal boolean `false`
      // (missing field, failed fetch, network error) means required.
      const required = result?.settings?.passwordRequired?.pageBuilder !== false;
      setCheckingRequirement(false);
      if (!required) {
        setStep("editor");
        loadSource(startPageId);
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const loadSource = async (jsonPageId) => {
    setLoading(true);
    setError("");
    try {
      const result = await getPageHtmlSourceAPI(pageId, token, jsonPageId);
      const manuallyEdited = Boolean(result.isManuallyEdited);
      const seed = !manuallyEdited ? getGeneratedHtml?.(jsonPageId) || "" : "";
      const serverDraft = result.html || seed || "";
      const sessionEntry = htmlSourcePages?.[jsonPageId];
      const initialDraft = sessionEntry?.sessionOnly && typeof sessionEntry.html === "string"
        ? sessionEntry.html
        : serverDraft;
      // Baseline for dirty/reset is the server value (or generated seed).
      // A session-only Apply may become the visible draft above it while
      // deliberately remaining dirty until Save persists it.
      setSavedHtml(serverDraft);
      setDraftHtml(initialDraft);
      setPreviewHtml(initialDraft);
      setIsManuallyEdited(manuallyEdited);
      setEditedAt(result.editedAt || null);
      setApplied(false);
      setConsoleEntries([]);
      setPreviewNonce((n) => n + 1);
    } catch (e) {
      setError(describeHtmlSourceError(e, e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPassword = async (event) => {
    event?.preventDefault?.();
    if (!password) return;
    setVerifying(true);
    setPasswordError("");
    try {
      const result = await verifyPageHtmlEditAccess(pageId, password, token);
      setConfirmationToken(result.confirmationToken);
      // Never keep the typed password in memory/state one moment longer than
      // needed to send it.
      setPassword("");
      setStep("editor");
      await loadSource(selectedJsonPageId);
    } catch (e) {
      setPasswordError(describeHtmlSourceError(e, elakayAutoText("h_pg_html_pw_fail", "Password verification failed.")));
    } finally {
      setVerifying(false);
    }
  };

  const dirty = draftHtml !== savedHtml;
  const analysis = useMemo(() => analyzePageHtml(draftHtml), [draftHtml]);

  const handleClose = () => {
    if (dirty && !window.confirm(elakayAutoText("h_pg_html_close_unsaved", "Close without saving? Applied changes remain available in this browser session, but will be lost after reload."))) return;
    onHide?.();
  };

  const handleSave = async () => {
    if (!analysis.valid) return;
    setSaving(true);
    setError("");
    try {
      const result = await savePageHtmlSourceAPI(pageId, draftHtml, confirmationToken, token, selectedJsonPageId, editedAt);
      const savedValue = typeof result.html === "string" ? result.html : draftHtml;
      setSavedHtml(savedValue);
      setDraftHtml(savedValue);
      setIsManuallyEdited(true);
      setEditedAt(result.editedAt || new Date().toISOString());
      setPreviewHtml(savedValue);
      setPreviewNonce((n) => n + 1);
      setApplied(true);
      onSaved?.(result.page);
    } catch (e) {
      setError(describeHtmlSourceError(e, e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async () => {
    if (!window.confirm(elakayAutoText("h_pg_html_restore_confirm", "Restore the generated page? Your manually edited HTML source will be discarded."))) return;
    setRestoring(true);
    setError("");
    try {
      const result = await restorePageGeneratedHtmlAPI(pageId, confirmationToken, token, selectedJsonPageId);
      onSaved?.(result.page);
      await loadSource(selectedJsonPageId);
    } catch (e) {
      setError(describeHtmlSourceError(e, e.message));
    } finally {
      setRestoring(false);
    }
  };

  // Quick Reset -- discards unsaved keystrokes and goes back to the last
  // loaded/saved state for THIS page, entirely client-side (no network call,
  // no confirmation dialog) -- distinct from "Reset to Generated Code" above,
  // which is a real server-side restore and does ask for confirmation.
  const handleQuickReset = () => {
    setDraftHtml(savedHtml);
    setPreviewHtml(savedHtml);
    if (applied) {
      onApplied?.({
        pageId: selectedJsonPageId,
        entry: isManuallyEdited ? { html: savedHtml, isManuallyEdited: true, editedAt } : null,
      });
    }
    setApplied(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(draftHtml);
      setCopyStatus(elakayAutoText("h_pg_html_copied", "Copied!"));
    } catch (_e) {
      setCopyStatus(elakayAutoText("h_pg_html_copy_failed", "Unable to copy."));
    }
    setTimeout(() => setCopyStatus(""), 2000);
  };

  // Switching pages -- each JSON page has its OWN independent draft on the
  // server, so this always re-fetches rather than reusing in-memory state.
  // Guards unsaved edits with the same discard-confirm convention used
  // throughout the codebase (e.g. HowWeDoItCodeEditorModal.jsx's
  // requestClose) rather than silently dropping them.
  const handleSelectPage = async (jsonPageId) => {
    if (jsonPageId === selectedJsonPageId) return;
    if (dirty && !window.confirm(elakayAutoText("h_pg_html_switch_page_confirm", "Discard unsaved changes to this page's HTML?"))) {
      return;
    }
    setSelectedJsonPageId(jsonPageId);
    await loadSource(jsonPageId);
  };

  // "+ Add new page" -- creates a brand new, empty JSON page in the Visual
  // Builder (onCreatePage === PageBuilder.jsx's own addPage(), returning the
  // new page's id), persists the document right away (onSaveDocument ===
  // the Builder's existing whole-document save) so the new page id is real
  // on the server BEFORE any htmlSourcePages entry can reference it, then
  // switches straight into editing its (empty, freshly generated) HTML --
  // a genuinely new, independent page the admin can make "complètement
  // différente" from every other page in this same document.
  const handleAddPage = async () => {
    if (!onCreatePage || creatingPage) return;
    if (dirty && !window.confirm(elakayAutoText("h_pg_html_switch_page_confirm", "Discard unsaved changes to this page's HTML?"))) {
      return;
    }
    setCreatingPage(true);
    setError("");
    try {
      const newId = onCreatePage();
      if (onSaveDocument) await onSaveDocument();
      setSelectedJsonPageId(newId);
      await loadSource(newId);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setCreatingPage(false);
    }
  };

  // Apply Code -- commits the current draft into the Preview immediately
  // (spec §6/§8).
  const handleApplyCode = () => {
    if (!analysis.valid) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setPreviewHtml(draftHtml);
    setPreviewNonce((n) => n + 1);
    setApplied(true);
    onApplied?.({
      pageId: selectedJsonPageId,
      entry: { html: draftHtml, isManuallyEdited: true, editedAt: editedAt || null, sessionOnly: true },
    });
  };

  // Refresh -- re-mounts the CURRENTLY APPLIED preview (a manual reload for a
  // page without re-syncing unsaved draft edits;
  // distinct from Apply Code, which pulls in the draft first.
  const handleRefreshPreview = () => {
    setPreviewNonce((n) => n + 1);
  };

  // A short idle-debounce auto-applies the draft too, so small edits still
  // reach the Preview without forcing a click every time -- but never on
  // every keystroke, which would rebuild the sandboxed iframe on a
  // multi-thousand-line document (spec §9's comfort requirement).
  useEffect(() => {
    if (step !== "editor") return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPreviewHtml(draftHtml);
      setPreviewNonce((n) => n + 1);
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftHtml, step]);

  // Cmd/Ctrl+S -- same universal expectation as HowWeDoItCodeEditorModal.jsx;
  // prevents the browser's own native "Save Page" dialog and triggers the
  // real Save above instead. Scoped to while this modal's editor step is
  // open, never a global listener.
  useEffect(() => {
    if (!show || step !== "editor") return undefined;
    const onKeyDown = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (dirty && !saving) handleSave();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, step, dirty, saving, draftHtml, confirmationToken, selectedJsonPageId]);

  const activeValue = useMemo(() => {
    if (activeTab === "console") return "";
    if (activeTab === "html") return getHtmlBodySection(draftHtml);
    if (activeTab === "css") return getHtmlCssSection(draftHtml);
    if (activeTab === "js") return getHtmlJsSection(draftHtml);
    return draftHtml;
  }, [activeTab, draftHtml]);

  const handleActiveChange = (value) => {
    if (activeTab === "html") setDraftHtml((prev) => setHtmlBodySection(prev, value));
    if (activeTab === "css") setDraftHtml((prev) => setHtmlCssSection(prev, value));
    else if (activeTab === "js") setDraftHtml((prev) => setHtmlJsSection(prev, value));
    else if (activeTab === "full") setDraftHtml(value);
  };

  // Format -- CSS tab only, same conservative reindenter as
  // howWeDoIt/HowWeDoItCodeEditorModal.jsx's own Format button, for the same
  // reason it disables Format on HTML/JS there: a generic HTML/JS beautifier
  // risks mangling markup/scripts it doesn't fully understand, CSS's grammar
  // is simple enough to reindent safely.
  const canFormat = activeTab === "css";
  const handleFormat = () => {
    if (!canFormat) return;
    setDraftHtml((prev) => setHtmlCssSection(prev, formatCssText(getHtmlCssSection(prev))));
  };

  const previewDoc = useMemo(() => securePageHtmlDocument(withConsoleShim(previewHtml)), [previewHtml]);

  useEffect(() => {
    if (!show || step !== "editor") return undefined;
    const receive = (event) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const payload = event.data;
      if (!payload || payload.source !== PAGE_BUILDER_SANDBOX_SOURCE) return;
      if (!["log", "warn", "error", "info"].includes(payload.kind) || !Array.isArray(payload.args)) return;
      setConsoleEntries((current) => [...current.slice(-199), {
        id: `${Date.now()}-${Math.random()}`,
        kind: payload.kind,
        message: payload.args.map(String).join(" ").slice(0, 4000),
        time: new Date().toLocaleTimeString(),
      }]);
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [show, step, previewNonce]);

  useEffect(() => {
    if (!show || step !== "editor" || !dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [show, step, dirty]);

  const handleImportFile = async (file) => {
    if (!file) return;
    setImportError("");
    if (file.size > PAGE_BUILDER_HTML_LIMIT) {
      setImportError(elakayAutoText("h_pg_html_import_too_large", "The imported file exceeds the 500,000 character limit."));
      return;
    }
    try {
      const text = await file.text();
      const result = analyzePageHtml(text);
      if (!result.valid) { setImportError(result.errors.map((item) => item.message).join(" ")); return; }
      if (dirty && !window.confirm(elakayAutoText("h_pg_html_import_replace", "Replace the current unsaved code with this file?"))) return;
      setDraftHtml(text);
      setActiveTab("full");
      setPreviewHtml(text);
      setPreviewNonce((n) => n + 1);
    } catch (e) {
      setImportError(e?.message || String(e));
    }
  };

  const handleExport = () => {
    const safeName = String(pageTitle || "page").trim().replace(/[^a-z0-9_-]+/gi, "-").replace(/^-|-$/g, "") || "page";
    downloadText(`${safeName}.html`, draftHtml);
  };

  const activeTabMeta = TABS.find((t) => t.id === activeTab) || TABS[0];

  const pageHasOverride = (id) =>
    Boolean(htmlSourcePages?.[id]?.isManuallyEdited) ||
    (pagesList[0]?.id === id && Boolean(legacyHtmlSource?.isManuallyEdited));

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size={step === "editor" ? "lg" : undefined}
      dialogClassName={step === "editor" ? `pb-html-modal-dialog${fullscreen ? " pb-html-modal-dialog--fullscreen" : ""}` : undefined}
      contentClassName={step === "editor" ? "pb-html-modal" : undefined}
      onEscapeKeyDown={(e) => {
        if (fullscreen) { e.preventDefault(); setFullscreen(false); }
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <Code2 size={17} style={{ marginRight: 6, verticalAlign: -3 }} />
          {step === "password"
            ? elakayAutoText("h_pg_html_secure_title", "Secure HTML Editing")
            : elakayAutoText("h_pg_html_edit_title", "Edit HTML Code")}
          {pageTitle && step === "editor" && (
            <span style={{ marginLeft: 10, fontSize: "12px", fontWeight: 500, color: "#9b93b8" }}>{pageTitle}</span>
          )}
        </Modal.Title>
      </Modal.Header>
      {step === "password" ? (
        <>
          <Modal.Body>
            {checkingRequirement ? (
              <div style={{ padding: "20px 0", textAlign: "center", color: "#9b93b8", fontSize: "13px" }}>
                {elakayAutoText("h_pg_html_checking_requirement", "Checking security requirements…")}
              </div>
            ) : (
            <form onSubmit={handleVerifyPassword}>
              {pageTitle && (
                <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "10px" }}>
                  {elakayAutoText("h_pg_html_page_label", "Page:")} <strong>{pageTitle}</strong>
                </div>
              )}
              <p style={{ fontSize: "13px", color: "#4b5563" }}>
                {elakayAutoText("h_pg_html_secure_desc", "This action allows direct HTML modification of the page. Confirm your account password to continue.")}
              </p>
              <label style={{ fontSize: "12px", fontWeight: 700, color: "#374151", display: "block", marginBottom: "4px" }}>
                {elakayAutoText("h_pg_html_password_label", "Password")}
              </label>
              <input
                type="password"
                autoFocus
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ width: "100%", padding: "9px 12px", fontSize: "13px", border: "1px solid #d1d5db", borderRadius: "8px", boxSizing: "border-box" }}
              />
              {passwordError && (
                <div style={{ color: "#e11d48", fontSize: "12px", marginTop: "8px" }}>{passwordError}</div>
              )}
            </form>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" onClick={handleClose} style={ghostBtn} disabled={verifying}>{elakayAutoText("h_pg_html_cancel", "Cancel")}</button>
            {!checkingRequirement && (
              <button type="button" onClick={handleVerifyPassword} style={primaryBtn} disabled={verifying || !password}>
                {verifying ? elakayAutoText("h_pg_html_verifying", "Verifying…") : elakayAutoText("h_pg_html_verify_continue", "Verify & Continue")}
              </button>
            )}
          </Modal.Footer>
        </>
      ) : (
        <>
          <div className="pb-html-modal-subbar">
            {/* Page selector -- each JSON page has its own independent HTML
                override; "+ Add new page" creates a brand new one for a
                completely different, custom-HTML page. */}
            <select
              className="pb-html-page-select"
              value={selectedJsonPageId || ""}
              onChange={(e) => {
                if (e.target.value === "__add__") handleAddPage();
                else handleSelectPage(e.target.value);
              }}
              disabled={loading || saving || restoring || creatingPage}
              title={elakayAutoText("h_pg_html_page_select_title", "Choose which page's HTML to edit")}
            >
              {pagesList.map((p) => (
                <option key={p.id} value={p.id}>
                  {(p.name || p.id)}{pageHasOverride(p.id) ? elakayAutoText("h_105f510cbf65b7", "• custom HTML") : ""}
                </option>
              ))}
              {onCreatePage && <option value="__add__">{elakayAutoText("h_pg_html_add_page", "+ Add new page…")}</option>}
            </select>
            <span style={{ fontSize: "11px", color: "#6b7280" }}>
              {isManuallyEdited
                ? `${elakayAutoText("h_pg_html_manually_edited", "Manually edited")}${editedAt ? ` · ${new Date(editedAt).toLocaleString()}` : ""}`
                : elakayAutoText("h_pg_html_generated", "Generated from the builder")}
            </span>
            <div className="pb-html-modal-tabs" role="tablist">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    className={`pb-html-modal-tab${activeTab === tab.id ? " active" : ""}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <Icon size={13} />
                    {elakayAutoText(tab.labelKey, tab.label)}
                  </button>
                );
              })}
            </div>
            <input
              ref={importInputRef}
              type="file"
              accept=".html,.htm,text/html,text/plain"
              hidden
              onChange={(event) => { handleImportFile(event.target.files?.[0]); event.target.value = ""; }}
            />
            <button type="button" className="pb-html-modal-toggle" onClick={() => importInputRef.current?.click()}>
              <Upload size={13} /> {elakayAutoText("h_pg_html_import", "Import")}
            </button>
            <button type="button" className="pb-html-modal-toggle" onClick={handleExport} disabled={!draftHtml.trim()}>
              <Download size={13} /> {elakayAutoText("h_pg_html_export", "Export")}
            </button>
            <div className="pb-html-device-switcher" role="group" aria-label={elakayAutoText("h_pg_html_preview_device", "Preview device")}>
              {DEVICE_MODES.map((mode) => {
                const DeviceIcon = mode.icon;
                return (
                  <button key={mode.id} type="button" className={previewDevice === mode.id ? "active" : ""} onClick={() => setPreviewDevice(mode.id)} title={mode.label} aria-label={mode.label}>
                    <DeviceIcon size={13} />
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className={`pb-html-modal-toggle${showPreview ? " active" : ""}`}
              onClick={() => setShowPreview((p) => !p)}
              title={elakayAutoText("h_pg_html_toggle_preview", "Show/hide the preview panel")}
            >
              {showPreview ? <Eye size={13} /> : <EyeOff size={13} />}
              {elakayAutoText("h_pg_html_preview_toggle", "Preview")}
            </button>
            <button
              type="button"
              className="pb-html-modal-toggle"
              onClick={() => setFullscreen((f) => !f)}
              title={fullscreen
                ? elakayAutoText("h_pg_html_exit_fullscreen", "Exit Fullscreen")
                : elakayAutoText("h_pg_html_fullscreen", "Fullscreen")}
            >
              {fullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
            </button>
          </div>

          {error && <div style={{ color: "#e11d48", fontSize: "12px", padding: "8px 16px 0" }}>{error}</div>}
          {importError && <div style={{ color: "#b91c1c", background: "#fef2f2", borderBottom: "1px solid #fecaca", fontSize: "11px", padding: "7px 16px" }}>{importError}</div>}
          <div style={{ color: "#6b4f00", background: "#fff8db", borderBottom: "1px solid #f3df98", fontSize: "11px", padding: "7px 16px" }}>
            {elakayAutoText("h_pg_html_scripts_isolated", "Custom JavaScript runs only inside a sandboxed opaque-origin iframe. Network connections, forms, cookies, tokens and the parent DOM remain unavailable.")}
          </div>
          {isManuallyEdited && (
            <div className="pb-html-conflict-note">
              <AlertTriangle size={13} />
              {elakayAutoText("h_pg_html_visual_priority", "Custom code is currently the rendering priority for this page. Graphical blocks remain preserved and can be restored; graphical edits do not overwrite this code.")}
            </div>
          )}

          {loading || creatingPage ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#9b93b8", fontSize: "13px" }}>
              {creatingPage
                ? elakayAutoText("h_pg_html_creating_page", "Creating the new page…")
                : elakayAutoText("h_pg_html_loading", "Loading HTML source…")}
            </div>
          ) : (
            <div className={`pb-html-modal-body${showPreview ? "" : " pb-html-modal-body--code-only"}`}>
              <div className={`pb-html-editor-pane${showPreview ? "" : " pb-html-editor-pane--full"}`}>
                {activeTab === "console" ? (
                  <div className="pb-html-console-pane" role="log" aria-live="polite">
                    {consoleEntries.length ? consoleEntries.map((entry) => (
                      <div key={entry.id} className={`pb-html-console-entry pb-html-console-entry--${entry.kind}`}>
                        <span className="pb-html-console-entry-time">{entry.time}</span>
                        <span>{entry.message}</span>
                      </div>
                    )) : <div className="pb-html-console-empty">{elakayAutoText("h_pg_html_console_empty", "No preview messages yet.")}</div>}
                  </div>
                ) : (
                  <textarea
                    ref={textareaRef}
                    className="pb-html-code-editor-textarea"
                    value={activeValue}
                    onChange={(e) => handleActiveChange(e.target.value)}
                    spellCheck={false}
                    autoCapitalize="off"
                    autoCorrect="off"
                    aria-label={elakayAutoText(activeTabMeta.labelKey, activeTabMeta.label)}
                  />
                )}
              </div>
              {showPreview && (
                <div className="pb-html-preview-pane">
                  {previewHtml.trim() ? (
                    <div className="pb-html-preview-stage">
                      <iframe
                        ref={iframeRef}
                        key={previewNonce}
                        title={elakayAutoText("h_pg_html_preview_title", "HTML preview")}
                        sandbox={PAGE_HTML_SANDBOX}
                        referrerPolicy="no-referrer"
                        srcDoc={previewDoc}
                        className="pb-html-preview-frame"
                        style={{ width: DEVICE_MODES.find((mode) => mode.id === previewDevice)?.width || "100%" }}
                      />
                    </div>
                  ) : (
                    <div className="pb-html-preview-placeholder">
                      {elakayAutoText("h_pg_html_preview_empty", "Nothing to preview yet.")}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="pb-html-modal-statusbar">
            <span className={`pb-html-status--${analysis.valid ? "valid" : "invalid"}`} style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
              {analysis.valid ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
              {analysis.valid
                ? elakayAutoText("h_pg_html_status_ready", "Ready")
                : analysis.errors.map((item) => item.message).join(" ")}
            </span>
            <span className="pb-html-size-indicator">{analysis.chars.toLocaleString()} chars · {analysis.bytes.toLocaleString()} bytes · {analysis.scriptCount} scripts</span>
            {dirty && <span className="pb-html-dirty-indicator">{elakayAutoText("h_pg_html_unsaved", "Unsaved changes")}</span>}
            {applied && dirty && <span className="pb-html-applied-indicator">{elakayAutoText("h_pg_html_applied_session", "Applied to session")}</span>}
            {analysis.warnings.length > 0 && <span className="pb-html-warning-indicator" title={analysis.warnings.map((item) => item.message).join("\n")}>{analysis.warnings.length} warning(s)</span>}
            <button type="button" onClick={handleCopy} style={{ ...ghostBtn, padding: "5px 10px", fontSize: "11px", marginLeft: "auto" }}>
              <Copy size={12} style={{ marginRight: "4px" }} />{copyStatus || elakayAutoText("h_pg_html_copy", "Copy")}
            </button>
          </div>

          <div className="pb-html-modal-footer">
            <button type="button" onClick={handleClose} style={ghostBtn} disabled={saving || restoring}>{elakayAutoText("h_pg_html_cancel", "Cancel")}</button>
            <button type="button" onClick={handleRestore} style={dangerBtn} disabled={saving || restoring || loading}>
              <RotateCcw size={13} style={{ marginRight: "4px" }} />{restoring ? elakayAutoText("h_pg_html_restoring", "Restoring…") : elakayAutoText("h_pg_html_reset_generated", "Reset to Generated Code")}
            </button>
            <button type="button" onClick={handleQuickReset} style={ghostBtn} disabled={!dirty} title={elakayAutoText("h_pg_html_quick_reset_title", "Discard unsaved edits, back to the last loaded state")}>
              <Undo2 size={13} style={{ marginRight: "4px" }} />{elakayAutoText("h_pg_html_quick_reset", "Reset")}
            </button>
            <button type="button" onClick={handleFormat} style={ghostBtn} disabled={!canFormat} title={canFormat ? undefined : elakayAutoText("h_pg_html_format_unavailable", "No formatter available for this tab")}>
              <Wand2 size={13} style={{ marginRight: "4px" }} />{elakayAutoText("h_pg_html_format", "Format")}
            </button>
            <div className="pb-html-modal-footer-spacer" />
            <button type="button" onClick={handleRefreshPreview} style={ghostBtn}>
              <RefreshCw size={13} style={{ marginRight: "4px" }} />{elakayAutoText("h_pg_html_refresh", "Refresh")}
            </button>
            <button type="button" onClick={handleApplyCode} style={ghostBtn} disabled={!analysis.valid}>
              <CheckCircle2 size={13} style={{ marginRight: "4px" }} />{elakayAutoText("h_pg_html_apply_code", "Apply Code")}
            </button>
            <button type="button" onClick={handleSave} style={primaryBtn} disabled={saving || !dirty || !analysis.valid}>
              <Save size={13} style={{ marginRight: "4px" }} />{saving ? elakayAutoText("h_pg_html_saving", "Saving…") : elakayAutoText("h_pg_html_apply_save", "Save")}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

const ghostBtn = { padding: "6px 12px", fontSize: "12px", fontWeight: 600, borderRadius: "8px", border: "1px solid #e4dcfb", background: "#fff", color: "#4a4a68", cursor: "pointer" };
const primaryBtn = { ...ghostBtn, border: "none", background: "#6d28d9", color: "#fff" };
const dangerBtn = { ...ghostBtn, border: "1px solid #fca5a5", color: "#e11d48" };

export default PageHtmlSourceModal;
